import { EventEmitter } from 'events'
import { inspect } from 'util'

type LogLevel = 'log' | 'warn' | 'error' | 'debug'

export class BufferedLogger {
    private buffer: CircularBuffer

    private debugLogger = {
        debug: console.debug,
    }

    // Store original console methods
    private originalConsole = {
        log: console.log.bind(console),
        warn: console.warn.bind(console),
        error: console.error.bind(console),
        debug: console.debug.bind(console),
    }

    private createLogMethod(level: LogLevel, consoleMethod: (...args: unknown[]) => void) {
        return (...args: unknown[]) => {
            // Buffer the log
            this.buffer.enqueue(level, args)

            // Call original console method to preserve stack trace
            consoleMethod(...args)
        }
    }

    constructor(bufferSize: number) {
        this.buffer = new CircularBuffer(bufferSize)
    }

    public getBufferAsString() {
        return this.buffer.toString()
    }

    public getLogger() {
        return {
            info: this.createLogMethod('log', this.originalConsole.log),
            warn: this.createLogMethod('warn', this.originalConsole.warn),
            debug: this.createLogMethod('debug', this.originalConsole.debug),
            error: this.createLogMethod('error', this.originalConsole.error),
            debugLog: (...args: unknown[]) => {
                this.buffer.enqueue('debug', args)
                this.debugLogger.debug(...args)
            },
        }
    }
}

class CircularBuffer {
    private buffer: ({ level: LogLevel; date: Date; args: unknown[] } | null)[] = []
    private readonly _size: number
    private _count = 0
    private head = 0
    private tail = 0

    constructor(size: number) {
        this._size = size
        this._count = 0
        this.buffer = new Array(size).fill(null)
        this.head = 0
        this.tail = 0
    }

    public get size(): number {
        return this._size
    }

    public get count(): number {
        return this._count
    }

    public enqueue(level: LogLevel, args: unknown[]): void {
        this.buffer[this.tail] = { level, date: new Date(), args }
        this.tail = (this.tail + 1) % this._size
        if (this._count === this._size) {
            this.head = (this.head + 1) % this._size
        } else {
            this._count++
        }
    }

    public clear(): void {
        this.buffer = new Array(this._size).fill(null)
        this._count = 0
        this.head = 0
        this.tail = 0
    }

    public isEmpty(): boolean {
        return this._count === 0
    }

    public isFull(): boolean {
        return this._count === this._size
    }

    public toString(): string {
        const delimiter = '\n'
        const results = []
        for (let i = 0; i < this.count; i++) {
            const index = (this.head + i) % this.size
            if (this.buffer[index] !== null) {
                const level = this.buffer[index].level
                const date = this.buffer[index].date
                let str = formatWithoutColors(
                    {
                        depth: 2,
                        showHidden: false,
                    },
                    this.buffer[index].args[0],
                    ...this.buffer[index].args.slice(1),
                )

                const maxLength = 100_000
                str = str.length > maxLength ? str.slice(0, maxLength) + '~~ TRUNCATED ~~' : str
                results.push(`[${date.toISOString()}] [${level}] ${str}`)
            }
        }
        return results.join(delimiter)
    }
}

const formatRegExp = /%[sdjcoO%]/g
/// grabbed from util.js and hacked to remove colors, print bigints and errors, etc
const formatWithoutColors = (
    opts: { showHidden?: boolean; depth?: number },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    f: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...args: any[]
): string => {
    if (typeof f !== 'string') {
        const objects = []
        for (let i = 0; i < args.length; i++) {
            objects.push(wrappedInspect(args[i], opts))
        }
        return objects.join(' ')
    }

    let i = 0
    const len = args.length

    const replacedValue = (x: string) => {
        if (x === '%%') {
            return '%'
        }
        if (i >= len) {
            return x
        }
        switch (x) {
            case '%s':
                return String(args[i++])
            case '%d':
                return `${Number(args[i++])}`
            case '%j':
                try {
                    return JSON.stringify(args[i++])
                } catch (_) {
                    return '[Circular]'
                }
            case '%c':
                i++
                return ''
            case '%O':
            case '%o':
                return wrappedInspect(args[i++], opts)
            default:
                return x
        }
    }
    let str = String(f).replace(formatRegExp, replacedValue)
    for (let x = args[i]; i < len; x = args[++i]) {
        if (!x || typeof x !== 'object') {
            str += ' ' + x
        } else {
            str += ' ' + wrappedInspect(x, opts)
        }
    }
    return str
}

const wrappedInspect = (value: unknown, opts: { showHidden?: boolean; depth?: number }) => {
    if (typeof value === 'bigint') {
        return value.toString()
    }

    if (value instanceof Error) {
        return value.toString() + '\n' + value.stack
    }

    if (value instanceof EventEmitter) {
        return 'EventEmitter'
    }

    return inspect(value, opts.showHidden, opts.depth, false)
}

export const bufferedLogger = new BufferedLogger(500)
